import type { ApiClient } from 'clients/yogi-api'
import type {
  IssueBreakdownResponse,
  IssuePostTextsResponse,
} from 'features/trend-detection/service/types'
import type {
  AgentCollaboratorsResponse,
  AgentSummaryResponse,
  BenchmarkingAgentDetail,
  BenchmarkingAgentRun,
  Chart,
  CreateAgentJobResponse,
  CreateIssueAgentRequest,
  InterpretFiltersResponse,
  IssueAgentResponse,
  IssueAgentRunDetailResponse,
  IssueAgentRunResponse,
  IssueTimeseriesChartResponse,
  IssueTrackingMutationResponse,
  KeyInsightItem,
  KeyInsightsResponse,
  RunIssueAgentRequest,
  SharedAgentResponse,
  ShareRunResponse,
  SubmitIssueFeedbackRequest,
  SubmitIssueFeedbackResponse,
  TrendFeedCountsResponse,
  TrendFeedIssueItem,
  TrendFeedSectionIssues,
  TrendFeedSectionUpdates,
  TrendFeedStatusResponse,
  UpdateDetailResponse,
  UpdateBenchmarkingAgentRequest,
  UpdateIssueAgentRequest,
} from 'features/trend-detection/types'

const TRENDS_URL = '/trends'
const BENCHMARKING_URL = '/benchmarking'
const INSIGHT_AGENT_URL = '/insight-agent'

export class TrendDetectionService {
  constructor(private client: ApiClient) {}

  /**
   * Fetch a specific run by UUID (shareable URL).
   * Returns light issue/update payloads; charts and verbatims via per-issue endpoints.
   */
  async fetchRunByUuid(
    projectId: string,
    runUuid: string,
  ): Promise<IssueAgentRunDetailResponse> {
    const { data } = await this.client.get<IssueAgentRunDetailResponse>(
      `${TRENDS_URL}/runs/${runUuid}`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Fetch all runs for a project.
   */
  async fetchRuns(
    projectId: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<IssueAgentRunResponse[]> {
    const config = { params: { proj_uuid: projectId, limit, offset } }

    const { data } = await this.client.get<IssueAgentRunResponse[]>(
      `${TRENDS_URL}/runs`,
      config,
    )
    return data
  }

  /**
   * Fetch the cross-agent high-signal feed shown in homepage/key insights.
   */
  async fetchKeyInsights(projectId: string): Promise<KeyInsightItem[]> {
    const { data } = await this.client.get<KeyInsightsResponse>(
      '/agent-center/key-insights',
      { params: { proj_uuid: projectId } },
    )
    return data.items
  }

  /**
   * Fetch runs for a specific agent.
   */
  async fetchAgentRuns(
    projectId: string,
    agentUuid: string,
    limit: number = 50,
  ): Promise<IssueAgentRunResponse[]> {
    const config = { params: { proj_uuid: projectId, limit } }

    const { data } = await this.client.get<IssueAgentRunResponse[]>(
      `${TRENDS_URL}/agents/${agentUuid}/runs`,
      config,
    )
    return data
  }

  /**
   * Create a new issue agent (saves configuration without running).
   */
  async createAgent(
    projectId: string,
    request: CreateIssueAgentRequest,
  ): Promise<IssueAgentResponse> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.post<IssueAgentResponse>(
      `${TRENDS_URL}/agents`,
      request,
      config,
    )
    return data
  }

  /**
   * Interpret a natural language prompt into structured filter criteria.
   * Used by the agent setup flow for "Autogenerate Filters".
   */
  async interpretFilters(
    projectId: string,
    prompt: string,
  ): Promise<InterpretFiltersResponse> {
    const { data } = await this.client.get<InterpretFiltersResponse>(
      `${TRENDS_URL}/agents/interpret-filters`,
      { params: { proj_uuid: projectId, prompt } },
    )
    return data
  }

  /**
   * Run an existing issue agent.
   * This triggers a Prefect job and returns immediately with the run UUID.
   * The issue detection pipeline runs asynchronously in the background.
   * Use fetchRunByUuid() to poll for results.
   */
  async runAgent(
    projectId: string,
    agentUuid: string,
    request: RunIssueAgentRequest = {},
  ): Promise<CreateAgentJobResponse> {
    const config = {
      params: { proj_uuid: projectId },
    }

    const { data } = await this.client.post<CreateAgentJobResponse>(
      `${TRENDS_URL}/agents/${agentUuid}/run`,
      request,
      config,
    )
    return data
  }

  /**
   * Fetch all agents for a project.
   */
  async fetchAgents(
    projectId: string,
    activeOnly: boolean = true,
  ): Promise<IssueAgentResponse[]> {
    const config = { params: { proj_uuid: projectId, active_only: activeOnly } }

    const { data } = await this.client.get<IssueAgentResponse[]>(
      `${TRENDS_URL}/agents`,
      config,
    )
    return data
  }

  /**
   * Fetch all agents with summary info (latest run, status, issue count).
   * Optimized endpoint that returns everything needed for the agents table.
   */
  async fetchAgentsSummary(
    projectId: string,
    activeOnly: boolean = true,
  ): Promise<AgentSummaryResponse[]> {
    const config = { params: { proj_uuid: projectId, active_only: activeOnly } }

    const { data } = await this.client.get<AgentSummaryResponse[]>(
      `${TRENDS_URL}/agents/summary`,
      config,
    )
    return data
  }

  /**
   * Project-level feed metadata: banners and agent filter list.
   * Does not depend on the selected agent filter.
   */
  async fetchFeedStatus(projectId: string): Promise<TrendFeedStatusResponse> {
    const { data } = await this.client.get<TrendFeedStatusResponse>(
      `${TRENDS_URL}/feed/status`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Section counts for the selected agent filter.
   * No internal IDs — only counts and a cache-bust timestamp.
   */
  async fetchFeedCounts(
    projectId: string,
    agentUuid?: string,
    emergenceLevel?: string,
    period: 'current' | 'all_time' = 'current',
  ): Promise<TrendFeedCountsResponse> {
    const params: Record<string, string> = {
      proj_uuid: projectId,
      period,
    }
    if (agentUuid) params['agent_uuid'] = agentUuid
    if (emergenceLevel) params['emergence_level'] = emergenceLevel
    const { data } = await this.client.get<TrendFeedCountsResponse>(
      `${TRENDS_URL}/feed/counts`,
      { params },
    )
    return data
  }

  async fetchFeedIssues(
    projectId: string,
    options?: {
      page?: number
      limit?: number
      sort?: 'emergence' | 'mentions'
      agentUuid?: string
      emergenceLevel?: string
      period?: 'current' | 'all_time'
    },
  ): Promise<TrendFeedSectionIssues> {
    const params: Record<string, string | number> = {
      proj_uuid: projectId,
      period: options?.period ?? 'current',
    }
    if (options?.agentUuid) params['agent_uuid'] = options.agentUuid
    if (options?.page != null) params['page'] = options.page
    if (options?.limit != null) params['limit'] = options.limit
    if (options?.sort) params['sort'] = options.sort
    if (options?.emergenceLevel)
      params['emergence_level'] = options.emergenceLevel
    const { data } = await this.client.get<TrendFeedSectionIssues>(
      `${TRENDS_URL}/feed/issues`,
      { params },
    )
    return data
  }

  async fetchFeedUpdates(
    projectId: string,
    options?: {
      page?: number
      limit?: number
      sort?:
        | 'most_changing'
        | 'records_first'
        | 'most_escalating'
        | 'most_improving'
      agentUuid?: string
    },
  ): Promise<TrendFeedSectionUpdates> {
    const params: Record<string, string | number> = {
      proj_uuid: projectId,
    }
    if (options?.agentUuid) params['agent_uuid'] = options.agentUuid
    if (options?.page != null) params['page'] = options.page
    if (options?.limit != null) params['limit'] = options.limit
    if (options?.sort) params['sort'] = options.sort
    const { data } = await this.client.get<TrendFeedSectionUpdates>(
      `${TRENDS_URL}/feed/updates`,
      { params },
    )
    return data
  }

  /**
   * Fetch a single agent by UUID.
   */
  /**
   * @param checkStaleFilters Ask the server to validate the agent's saved
   *   filters against the project's current options. Off by default: the
   *   check costs a filter-options fetch plus a theme-resolution query, and
   *   only the agent config page renders the result — the run pages read
   *   this same endpoint and would pay for a verdict they discard.
   */
  async fetchAgent(
    projectId: string,
    agentUuid: string,
    { checkStaleFilters = false }: { checkStaleFilters?: boolean } = {},
  ): Promise<IssueAgentResponse> {
    const config = {
      params: {
        proj_uuid: projectId,
        ...(checkStaleFilters && { check_stale_filters: true }),
      },
    }

    const { data } = await this.client.get<IssueAgentResponse>(
      `${TRENDS_URL}/agents/${agentUuid}`,
      config,
    )
    return data
  }

  /**
   * Update an existing issue agent.
   * If description changes, backend will re-interpret filters.
   */
  async updateAgent(
    projectId: string,
    agentUuid: string,
    request: UpdateIssueAgentRequest,
  ): Promise<IssueAgentResponse> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.patch<IssueAgentResponse>(
      `${TRENDS_URL}/agents/${agentUuid}`,
      request,
      config,
    )
    return data
  }

  async updateEmailPreference(
    projectId: string,
    agentUuid: string,
    emailNotifications: boolean,
  ): Promise<{ email_notifications: boolean }> {
    const config = { params: { proj_uuid: projectId } }
    const { data } = await this.client.put<{ email_notifications: boolean }>(
      `${TRENDS_URL}/agents/${agentUuid}/email-preference`,
      { email_notifications: emailNotifications },
      config,
    )
    return data
  }

  /**
   * Fetch shared agent detail (read-only, viewer access).
   * Returns agent and most recent run with light issue payloads; charts/verbatims via per-issue endpoints.
   */
  async fetchSharedAgent(
    projectId: string,
    agentUuid: string,
  ): Promise<SharedAgentResponse> {
    const { data } = await this.client.get<SharedAgentResponse>(
      `${TRENDS_URL}/agents/shared/${agentUuid}`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Fetch shared run detail (read-only, viewer access).
   * Returns light issue/update payloads; charts and verbatims via per-issue endpoints.
   */
  async fetchSharedRun(
    projectId: string,
    runUuid: string,
  ): Promise<IssueAgentRunDetailResponse> {
    const { data } = await this.client.get<IssueAgentRunDetailResponse>(
      `${TRENDS_URL}/runs/shared/${runUuid}`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Fetch a single issue detail (with run_uuid, agent info) for the detail page.
   */
  async fetchIssueDetail(
    projectId: string,
    issueUuid: string,
  ): Promise<TrendFeedIssueItem> {
    const { data } = await this.client.get<TrendFeedIssueItem>(
      `${TRENDS_URL}/issues/${issueUuid}`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Fetch a single issue update detail (with run_uuid, agent info) for the detail page.
   */
  async fetchUpdateDetail(
    projectId: string,
    updateUuid: string,
  ): Promise<UpdateDetailResponse> {
    const { data } = await this.client.get<UpdateDetailResponse>(
      `${TRENDS_URL}/updates/${updateUuid}`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Fetch timeseries charts for multiple issues in one request (batch).
   * Returns a map of issue_uuid -> Chart. Only includes issues that were found.
   */
  async fetchIssueTimeseriesChartsBatch(
    projectId: string,
    runUuid: string,
    issueUuids: string[],
  ): Promise<{
    charts: Record<string, Chart>
    projectTotalChart: Chart | null
  }> {
    if (issueUuids.length === 0) return { charts: {}, projectTotalChart: null }
    const { data } = await this.client.post<{
      charts: Record<string, Chart>
      project_total_chart: Chart | null
    }>(
      `${TRENDS_URL}/runs/${runUuid}/timeseries_charts`,
      { issue_uuids: issueUuids },
      { params: { proj_uuid: projectId } },
    )
    return {
      charts: data.charts ?? {},
      projectTotalChart: data.project_total_chart ?? null,
    }
  }

  /**
   * Fetch timeseries chart for one issue (lazy load).
   */
  async fetchIssueTimeseriesChart(
    projectId: string,
    runUuid: string,
    issueUuid: string,
  ): Promise<IssueTimeseriesChartResponse> {
    const { data } = await this.client.get<IssueTimeseriesChartResponse>(
      `${TRENDS_URL}/runs/${runUuid}/issues/${issueUuid}/timeseries_chart`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Fetch breakdown chart for one issue by specified dimension (lazy load).
   * For custom fields, pass dimension as product_custom/source_custom/post_custom/post_text_custom and customField as the field name.
   */
  async fetchIssueBreakdowns(
    projectId: string,
    runUuid: string,
    issueUuid: string,
    dimension: string,
    customField?: string,
  ): Promise<IssueBreakdownResponse> {
    const params: Record<string, string> = {
      proj_uuid: projectId,
      dimension,
    }
    if (customField != null) params['custom_field'] = customField
    const { data } = await this.client.get<IssueBreakdownResponse>(
      `${TRENDS_URL}/runs/${runUuid}/issues/${issueUuid}/breakdowns`,
      { params },
    )
    return data
  }

  /**
   * Fetch verbatims (post_texts) for one issue (lazy load).
   */
  async fetchIssuePostTexts(
    projectId: string,
    runUuid: string,
    issueUuid: string,
  ): Promise<IssuePostTextsResponse> {
    const { data } = await this.client.get<IssuePostTextsResponse>(
      `${TRENDS_URL}/runs/${runUuid}/issues/${issueUuid}/post_texts`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Fetch timeseries charts for multiple issues in a shared run (batch).
   */
  async fetchSharedIssueTimeseriesChartsBatch(
    projectId: string,
    runUuid: string,
    issueUuids: string[],
  ): Promise<{
    charts: Record<string, Chart>
    projectTotalChart: Chart | null
  }> {
    if (issueUuids.length === 0) return { charts: {}, projectTotalChart: null }
    const { data } = await this.client.post<{
      charts: Record<string, Chart>
      project_total_chart: Chart | null
    }>(
      `${TRENDS_URL}/runs/shared/${runUuid}/timeseries_charts`,
      { issue_uuids: issueUuids },
      { params: { proj_uuid: projectId } },
    )
    return {
      charts: data.charts ?? {},
      projectTotalChart: data.project_total_chart ?? null,
    }
  }

  /**
   * Fetch timeseries chart for one issue in a shared run (lazy load).
   */
  async fetchSharedIssueTimeseriesChart(
    projectId: string,
    runUuid: string,
    issueUuid: string,
  ): Promise<IssueTimeseriesChartResponse> {
    const { data } = await this.client.get<IssueTimeseriesChartResponse>(
      `${TRENDS_URL}/runs/shared/${runUuid}/issues/${issueUuid}/timeseries_chart`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Fetch breakdown chart for one issue by specified dimension in a shared run (lazy load).
   * For custom fields, pass dimension as product_custom/... and customField as the field name.
   */
  async fetchSharedIssueBreakdowns(
    projectId: string,
    runUuid: string,
    issueUuid: string,
    dimension: string,
    customField?: string,
  ): Promise<IssueBreakdownResponse> {
    const params: Record<string, string> = {
      proj_uuid: projectId,
      dimension,
    }
    if (customField != null) params['custom_field'] = customField
    const { data } = await this.client.get<IssueBreakdownResponse>(
      `${TRENDS_URL}/runs/shared/${runUuid}/issues/${issueUuid}/breakdowns`,
      { params },
    )
    return data
  }

  /**
   * Fetch verbatims (post_texts) for one issue in a shared run (lazy load).
   */
  async fetchSharedIssuePostTexts(
    projectId: string,
    runUuid: string,
    issueUuid: string,
  ): Promise<IssuePostTextsResponse> {
    const { data } = await this.client.get<IssuePostTextsResponse>(
      `${TRENDS_URL}/runs/shared/${runUuid}/issues/${issueUuid}/post_texts`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Toggle sharing status for a run.
   * When shared=true, marks the run as shared and generates a shareable URL.
   * When shared=false, unshares the run.
   */
  async shareRun(
    projectId: string,
    runUuid: string,
    shared: boolean,
  ): Promise<ShareRunResponse> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.put<ShareRunResponse>(
      `${TRENDS_URL}/runs/${runUuid}/share`,
      { shared },
      config,
    )
    return data
  }

  /**
   * Start tracking an issue for the current user (per agent).
   */
  async trackIssue(
    projectId: string,
    issueUuid: string,
  ): Promise<IssueTrackingMutationResponse> {
    const config = { params: { proj_uuid: projectId } }
    const { data } = await this.client.post<IssueTrackingMutationResponse>(
      `${TRENDS_URL}/issues/${issueUuid}/track`,
      {},
      config,
    )
    return data
  }

  /**
   * Stop tracking an issue for the current user.
   */
  async untrackIssue(
    projectId: string,
    issueUuid: string,
  ): Promise<IssueTrackingMutationResponse> {
    const config = { params: { proj_uuid: projectId } }
    const { data } = await this.client.post<IssueTrackingMutationResponse>(
      `${TRENDS_URL}/issues/${issueUuid}/untrack`,
      {},
      config,
    )
    return data
  }

  /**
   * Record the user's answer to the track-with-email opt-in modal.
   *
   * optIn=true turns on email_notifications for the agent. Either answer
   * permanently suppresses the modal for this user+agent pair.
   */
  async answerEmailPrompt(
    projectId: string,
    agentUuid: string,
    optIn: boolean,
  ): Promise<{ email_notifications: boolean; email_prompt_answered: boolean }> {
    const { data } = await this.client.post<{
      email_notifications: boolean
      email_prompt_answered: boolean
    }>(
      `${TRENDS_URL}/agents/${agentUuid}/email-prompt/answer`,
      { opt_in: optIn },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * Submit feedback for an issue.
   * Users can provide thumbs up/down feedback with optional additional details.
   * Multiple feedback submissions per user per issue are allowed.
   */
  async submitIssueFeedback(
    projectId: string,
    issueUuid: string,
    request: SubmitIssueFeedbackRequest,
  ): Promise<SubmitIssueFeedbackResponse> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.post<SubmitIssueFeedbackResponse>(
      `${TRENDS_URL}/issues/${issueUuid}/feedback`,
      request,
      config,
    )
    return data
  }

  // =========================================================================
  // Agent sharing
  // =========================================================================

  /** Fetch all collaborators (editors + viewers) for an agent. */
  async fetchAgentCollaborators(
    projectId: string,
    agentUuid: string,
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.get<AgentCollaboratorsResponse>(
      `${TRENDS_URL}/agents/${agentUuid}/collaborators`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /** Share an agent with users (adds them as viewers). */
  async shareAgent(
    projectId: string,
    agentUuid: string,
    emailAddresses: string[],
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.post<AgentCollaboratorsResponse>(
      `${TRENDS_URL}/agents/${agentUuid}/share`,
      { email_addresses: emailAddresses },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /** Remove users from an agent (editors only). */
  async unshareAgent(
    projectId: string,
    agentUuid: string,
    emailAddresses: string[],
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.post<AgentCollaboratorsResponse>(
      `${TRENDS_URL}/agents/${agentUuid}/unshare`,
      { email_addresses: emailAddresses },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  // =========================================================================
  // Benchmarking agent sharing
  // =========================================================================

  async fetchBenchmarkingAgentCollaborators(
    projectId: string,
    agentUuid: string,
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.get<AgentCollaboratorsResponse>(
      `${BENCHMARKING_URL}/agents/${agentUuid}/collaborators`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  async shareBenchmarkingAgent(
    projectId: string,
    agentUuid: string,
    emailAddresses: string[],
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.post<AgentCollaboratorsResponse>(
      `${BENCHMARKING_URL}/agents/${agentUuid}/share`,
      { email_addresses: emailAddresses },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  async unshareBenchmarkingAgent(
    projectId: string,
    agentUuid: string,
    emailAddresses: string[],
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.post<AgentCollaboratorsResponse>(
      `${BENCHMARKING_URL}/agents/${agentUuid}/unshare`,
      { email_addresses: emailAddresses },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  async fetchInsightAgentCollaborators(
    projectId: string,
    agentUuid: string,
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.get<AgentCollaboratorsResponse>(
      `${INSIGHT_AGENT_URL}/agents/${agentUuid}/collaborators`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  async shareInsightAgent(
    projectId: string,
    agentUuid: string,
    emailAddresses: string[],
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.post<AgentCollaboratorsResponse>(
      `${INSIGHT_AGENT_URL}/agents/${agentUuid}/share`,
      { email_addresses: emailAddresses },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  async unshareInsightAgent(
    projectId: string,
    agentUuid: string,
    emailAddresses: string[],
  ): Promise<AgentCollaboratorsResponse> {
    const { data } = await this.client.post<AgentCollaboratorsResponse>(
      `${INSIGHT_AGENT_URL}/agents/${agentUuid}/unshare`,
      { email_addresses: emailAddresses },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /**
   * List runs for a specific benchmarking agent (newest-first).
   */
  async fetchBenchmarkingAgentRuns(
    projectId: string,
    agentUuid: string,
    limit: number = 50,
  ): Promise<BenchmarkingAgentRun[]> {
    const { data } = await this.client.get<BenchmarkingAgentRun[]>(
      `${BENCHMARKING_URL}/agents/${agentUuid}/runs`,
      { params: { proj_uuid: projectId, limit } },
    )
    return data
  }

  /** Fetch a benchmarking agent's full config for editing/prepopulation. */
  async fetchBenchmarkingAgent(
    projectId: string,
    agentUuid: string,
  ): Promise<BenchmarkingAgentDetail> {
    const { data } = await this.client.get<BenchmarkingAgentDetail>(
      `${BENCHMARKING_URL}/agents/${agentUuid}`,
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  /** Update an existing benchmarking agent's name and/or filters. */
  async updateBenchmarkingAgent(
    projectId: string,
    agentUuid: string,
    request: UpdateBenchmarkingAgentRequest,
  ): Promise<void> {
    await this.client.patch(
      `${BENCHMARKING_URL}/agents/${agentUuid}`,
      request,
      { params: { proj_uuid: projectId } },
    )
  }

  async deleteBenchmarkingAgent(
    projectId: string,
    agentUuid: string,
  ): Promise<void> {
    await this.client.delete(`${BENCHMARKING_URL}/agents/${agentUuid}`, {
      params: { proj_uuid: projectId },
    })
  }

  /**
   * Run an existing benchmarking agent.
   * This triggers a Prefect job and returns immediately; the benchmarking
   * pipeline runs asynchronously in the background.
   */
  async runBenchmarkingAgent(
    projectId: string,
    agentUuid: string,
  ): Promise<void> {
    await this.client.post(
      `${BENCHMARKING_URL}/agents/${agentUuid}/run`,
      {},
      { params: { proj_uuid: projectId } },
    )
  }

  async deleteIssueAgent(projectId: string, agentUuid: string): Promise<void> {
    await this.client.delete(`${TRENDS_URL}/agents/${agentUuid}`, {
      params: { proj_uuid: projectId },
    })
  }

  async setAgentEnabled(
    projectId: string,
    agentUuid: string,
    isEnabled: boolean,
  ): Promise<{ is_enabled: boolean }> {
    const { data } = await this.client.put<{ is_enabled: boolean }>(
      `${TRENDS_URL}/agents/${agentUuid}/enabled`,
      { is_enabled: isEnabled },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  async setBenchmarkingAgentEnabled(
    projectId: string,
    agentUuid: string,
    isEnabled: boolean,
  ): Promise<{ is_enabled: boolean }> {
    const { data } = await this.client.put<{ is_enabled: boolean }>(
      `${BENCHMARKING_URL}/agents/${agentUuid}/enabled`,
      { is_enabled: isEnabled },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  async setBenchmarkingAgentEmailPreference(
    projectId: string,
    agentUuid: string,
    emailNotifications: boolean,
  ): Promise<{ email_notifications: boolean }> {
    const { data } = await this.client.put<{ email_notifications: boolean }>(
      `${BENCHMARKING_URL}/agents/${agentUuid}/email-preference`,
      { email_notifications: emailNotifications },
      { params: { proj_uuid: projectId } },
    )
    return data
  }

  async getBenchmarkingConversations(projectId: string): Promise<{
    conversations: Array<{
      uuid: string
      title: string
      create_time: number
      last_update: number
      entity_name: string | null
    }>
  }> {
    const { data } = await this.client.get<{
      conversations: Array<{
        uuid: string
        title: string
        create_time: number
        last_update: number
        entity_name: string | null
      }>
    }>(`${BENCHMARKING_URL}/conversations`, {
      params: { proj_uuid: projectId },
    })
    return data
  }

  async getBenchmarkingConversation(
    insightUuid: string,
    projectId: string,
  ): Promise<{ messages: Array<{ role: string; content: string }> }> {
    const { data } = await this.client.get<{
      messages: Array<{ role: string; content: string }>
    }>(`${BENCHMARKING_URL}/insights/${insightUuid}/conversation`, {
      params: { proj_uuid: projectId },
    })
    return data
  }

  async sendBenchmarkingFollowUp(
    insightUuid: string,
    projectId: string,
    question: string,
  ): Promise<{ answer: string }> {
    const { data } = await this.client.post<{ answer: string }>(
      `${BENCHMARKING_URL}/insights/${insightUuid}/chat`,
      { question },
      { params: { proj_uuid: projectId } },
    )
    return data
  }
}
